import React, { useCallback, useEffect, useState } from 'react';

/**
 * Staff authoring of packaged answers (tracker #107 slice C, owner ruling
 * D3): create, edit, publish/unpublish. Every save bumps the answer's
 * version so a customer's override can tell its original moved on.
 * "Promote to answer" from a resolved ticket lands here with a prefilled
 * draft — staff redact before publishing; nothing customer-specific ships.
 */

export interface StaffAnswer {
  id: string;
  title: string;
  summary: string;
  steps: string[];
  matchCodes: string[];
  matchKeywords: string[];
  docsUrl?: string;
  family: string | null;
  audience: 'user' | 'admin' | 'both';
  isPublished: boolean;
  version: number;
  sortOrder: number;
  updatedAtUtc: string;
  updatedBy: string | null;
}

export interface StaffAnswerDraft {
  id: string;
  title: string;
  summary: string;
  steps: string;
  matchCodes: string;
  matchKeywords: string;
  docsUrl: string;
  family: string;
  audience: 'user' | 'admin' | 'both';
  isPublished: boolean;
  sortOrder: string;
}

export const emptyAnswerDraft = (): StaffAnswerDraft => ({
  id: '', title: '', summary: '', steps: '', matchCodes: '', matchKeywords: '', docsUrl: '', family: '', audience: 'both', isPublished: false, sortOrder: '100'
});

const toDraft = (answer: StaffAnswer): StaffAnswerDraft => ({
  id: answer.id,
  title: answer.title,
  summary: answer.summary,
  steps: answer.steps.join('\n'),
  matchCodes: answer.matchCodes.join(', '),
  matchKeywords: answer.matchKeywords.join(', '),
  docsUrl: answer.docsUrl ?? '',
  family: answer.family ?? '',
  audience: answer.audience,
  isPublished: answer.isPublished,
  sortOrder: String(answer.sortOrder)
});

/** Pure: the draft as the API expects it. Exported for tests. */
export const draftToPayload = (draft: StaffAnswerDraft): Record<string, unknown> => ({
  id: draft.id.trim().toLowerCase(),
  title: draft.title.trim(),
  summary: draft.summary.trim(),
  steps: draft.steps.split(/\r?\n/).map((line) => line.trim()).filter(Boolean),
  matchCodes: draft.matchCodes.split(/[,;]/).map((code) => code.trim()).filter(Boolean),
  matchKeywords: draft.matchKeywords.split(/[,;]/).map((keyword) => keyword.trim()).filter(Boolean),
  docsUrl: draft.docsUrl.trim() || null,
  family: draft.family.trim() || null,
  audience: draft.audience,
  isPublished: draft.isPublished,
  sortOrder: Number(draft.sortOrder) || 100
});

/** Pure: an id suggestion from a title. Exported for tests. */
export const suggestAnswerId = (title: string, family?: string): string => {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
  const prefix = (family ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return prefix && !slug.startsWith(`${prefix}-`) ? `${prefix}-${slug}` : slug;
};

const readErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  const body = (await response.json().catch(() => ({}))) as { message?: string };
  return body.message ?? fallback;
};

interface Props {
  /** A prefilled draft handed in by "Promote to answer". */
  initialDraft?: StaffAnswerDraft;
  onBack: () => void;
}

const StaffAnswersPanel = ({ initialDraft, onBack }: Props): JSX.Element => {
  const [answers, setAnswers] = useState<StaffAnswer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState<StaffAnswerDraft | undefined>(initialDraft);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await fetch('/api/admin/support/answers');
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Answers API returned ${response.status}`));
      }
      const body = (await response.json()) as { answers?: StaffAnswer[] };
      setAnswers(Array.isArray(body.answers) ? body.answers : []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (): Promise<void> => {
    if (!draft) return;
    setWorking(true);
    setError(undefined);
    try {
      const payload = draftToPayload({ ...draft, id: draft.id.trim() || suggestAnswerId(draft.title, draft.family) });
      const response = await fetch('/api/admin/support/answers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Save failed (${response.status})`));
      }
      setNotice(`Saved "${payload.title as string}"${draft.isPublished ? ' (published)' : ' (draft)'}.`);
      setDraft(undefined);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const togglePublished = async (answer: StaffAnswer): Promise<void> => {
    setWorking(true);
    setError(undefined);
    try {
      const response = await fetch(`/api/admin/support/answers/${encodeURIComponent(answer.id)}/published`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPublished: !answer.isPublished })
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Update failed (${response.status})`));
      }
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const visible = answers.filter((answer) => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return true;
    return `${answer.id} ${answer.title} ${answer.family ?? ''} ${answer.matchCodes.join(' ')}`.toLowerCase().includes(needle);
  });

  return (
    <div className="card" style={{ marginTop: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0 }}>Answers</h3>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            Packaged self-service answers served to every installation. Publishing bumps the version; customers
            who overrode an answer see that the original moved on.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button type="button" className="btn secondary" onClick={() => setDraft(emptyAnswerDraft())} disabled={working}>New answer</button>
          <button type="button" className="btn secondary" onClick={onBack}>Back to queue</button>
        </div>
      </div>
      {error && <div className="pill warning" style={{ marginTop: '8px' }}>{error}</div>}
      {notice && <div className="pill success" style={{ marginTop: '8px' }}>{notice}</div>}

      {draft && (
        <div className="card" style={{ marginTop: '12px', display: 'grid', gap: '8px', maxWidth: '760px' }}>
          <strong>{draft.id ? `Edit ${draft.id}` : 'New answer'}</strong>
          <label className="muted">Id (lowercase, hyphens)
            <input className="input" value={draft.id} onChange={(e) => setDraft({ ...draft, id: e.target.value })} placeholder={suggestAnswerId(draft.title, draft.family) || 'family-short-name'} disabled={working} />
          </label>
          <label className="muted">Title
            <input className="input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} maxLength={200} disabled={working} />
          </label>
          <label className="muted">Summary
            <textarea className="input" rows={3} value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} maxLength={1000} disabled={working} />
          </label>
          <label className="muted">Steps (one per line)
            <textarea className="input" rows={5} value={draft.steps} onChange={(e) => setDraft({ ...draft, steps: e.target.value })} disabled={working} />
          </label>
          <label className="muted">Match codes (comma separated, e.g. LICENSE_EXPIRED)
            <input className="input" value={draft.matchCodes} onChange={(e) => setDraft({ ...draft, matchCodes: e.target.value })} disabled={working} />
          </label>
          <label className="muted">Match keywords (comma separated)
            <input className="input" value={draft.matchKeywords} onChange={(e) => setDraft({ ...draft, matchKeywords: e.target.value })} disabled={working} />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px' }}>
            <label className="muted">Docs path
              <input className="input" value={draft.docsUrl} onChange={(e) => setDraft({ ...draft, docsUrl: e.target.value })} placeholder="/docs?path=docs/documentation/users/faq.md" disabled={working} />
            </label>
            <label className="muted">Family
              <input className="input" value={draft.family} onChange={(e) => setDraft({ ...draft, family: e.target.value })} placeholder="licence, install, permissions…" disabled={working} />
            </label>
            <label className="muted">Audience
              <select className="input" value={draft.audience} onChange={(e) => setDraft({ ...draft, audience: e.target.value as StaffAnswerDraft['audience'] })} disabled={working}>
                <option value="both">Everyone</option>
                <option value="user">Users</option>
                <option value="admin">Administrators</option>
              </select>
            </label>
            <label className="muted">Sort order
              <input className="input" value={draft.sortOrder} onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })} disabled={working} />
            </label>
          </div>
          <label className="muted" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <input type="checkbox" checked={draft.isPublished} onChange={(e) => setDraft({ ...draft, isPublished: e.target.checked })} disabled={working} />
            Published (visible to every installation)
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" className="cta" onClick={() => void save()} disabled={working || !draft.title.trim() || !draft.summary.trim()}>
              {working ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn secondary" onClick={() => setDraft(undefined)} disabled={working}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
        <input className="input" placeholder="Filter by id, title, family or code" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ maxWidth: '360px' }} />
        <span className="muted">{visible.length} of {answers.length}</span>
      </div>
      {loading && <span className="badge-subtle" style={{ marginTop: '10px' }}>Loading answers…</span>}
      {!loading && answers.length === 0 && !error && (
        <p className="muted" style={{ marginTop: '10px' }}>No answers in SQL yet — the shelf is serving the static fallback. Apply V101/V102 or add one.</p>
      )}
      <div style={{ display: 'grid', gap: '8px', marginTop: '10px' }}>
        {visible.map((answer) => (
          <div key={answer.id} className="list-row">
            <div>
              <strong>{answer.title}</strong>
              <div className="muted" style={{ fontSize: '0.85rem', marginTop: '4px' }}>
                <code>{answer.id}</code>
                {answer.family ? ` · ${answer.family}` : ''}
                {` · v${answer.version}`}
                {answer.matchCodes.length ? ` · ${answer.matchCodes.join(', ')}` : ''}
                {answer.updatedBy ? ` · ${answer.updatedBy}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span className={answer.isPublished ? 'pill success' : 'badge-subtle'}>{answer.isPublished ? 'Published' : 'Draft'}</span>
              <button type="button" className="btn secondary" onClick={() => setDraft(toDraft(answer))} disabled={working}>Edit</button>
              <button type="button" className="btn secondary" onClick={() => void togglePublished(answer)} disabled={working}>
                {answer.isPublished ? 'Unpublish' : 'Publish'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StaffAnswersPanel;
